import React, { useState } from "react";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";

type VenueEventListCardProps = {
  eventName: string;
  bannerImg: string;
  eventStatus: string;
  eventDate: string;
  totalTicket: number;
  soldTicket: number;
  eventId: number;
  eventData: any;
  eventShortDescription: string;
  eventTime: any;
  onDelete?: (eventId: number) => void;
  onEdit?: (eventData: any) => void;
  canEdit?: boolean;
};

/** Status badge colors for dark glass theme */
const getStatusStyling = (status: string) => {
  switch (status) {
    case "Draft":
      return {
        backgroundColor: "rgba(245, 158, 11, 0.15)",
        borderColor: "rgba(245, 158, 11, 0.45)",
        color: "#fbbf24",
        icon: "fa-solid fa-edit",
      };
    case "Live":
      return {
        backgroundColor: "rgba(16, 185, 129, 0.18)",
        borderColor: "rgba(16, 185, 129, 0.45)",
        color: "#34d399",
        icon: "fa-solid fa-circle-check",
      };
    default:
      return {
        backgroundColor: "rgba(255, 255, 255, 0.06)",
        borderColor: "rgba(255, 255, 255, 0.12)",
        color: "rgba(255, 255, 255, 0.7)",
        icon: "fa-solid fa-circle",
      };
  }
};

/** Strip HTML so rich descriptions don't dump raw <p>/<br> into the card */
const stripHtml = (html: string) => {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/p>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
};

/**
 * Horizontal venue event card.
 * Actions always sit in a fixed right column so Edit/Delete never jump around.
 * Edit only shows for venue-owned list events (canEdit).
 */
const VenueEventListCard = (props: VenueEventListCardProps) => {
  const {
    eventName,
    bannerImg,
    eventStatus,
    eventDate,
    eventId,
    eventTime,
    eventShortDescription,
    onDelete,
    onEdit,
    canEdit = false,
  } = props;
  const styling = getStatusStyling(eventStatus);
  const [modal, setModal] = useState(false);
  const toggleModal = () => setModal(!modal);
  const plainDescription = stripHtml(eventShortDescription || "");

  const formatDateTime = (dateStr: string, timeStr?: string) => {
    if (!timeStr) return dateStr;
    const date = new Date(`${dateStr}T${timeStr}`);
    return `${date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "2-digit",
      year: "numeric",
    })} ${date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;
  };

  const displayDate = formatDateTime(eventDate, eventTime);

  const handleDelete = async () => {
    try {
      if (onDelete) {
        await onDelete(eventId);
        toggleModal();
      } else {
        console.warn("onDelete prop missing for VenueEventListCard");
      }
    } catch (error) {
      console.error("Failed to delete event:", error);
      toast.error("Failed to delete event");
    }
  };

  return (
    <div className="main-card ex-list-card">
      <div className="ex-list-card-inner">
        <div className="card-event-img">
          <img src={bannerImg} alt={eventName} />
        </div>

        <div className="ex-list-card-body">
          <div className="ex-list-card-top">
            <div className="ex-list-card-copy">
              <h5>{eventName}</h5>
              {plainDescription ? (
                <p className="ex-list-card-desc">{plainDescription}</p>
              ) : null}

              <div className="ex-list-card-meta">
                <span
                  className="ex-status-badge"
                  style={{
                    backgroundColor: styling.backgroundColor,
                    color: styling.color,
                    border: `1px solid ${styling.borderColor}`,
                  }}
                >
                  <i className={styling.icon}></i>
                  {eventStatus}
                </span>
                <span className="ex-meta-date">
                  <i className="fa-solid fa-calendar-days" aria-hidden="true"></i>
                  <span>
                    <em>Starts on</em>
                    {displayDate}
                  </span>
                </span>
              </div>
            </div>

            {/* Fixed action column — same spot on every card */}
            <div className="event-btn-group ex-list-card-actions">
              {canEdit && onEdit ? (
                <button
                  className="esv-btn"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(props.eventData);
                  }}
                >
                  <i className="fa-solid fa-edit me-2"></i>Edit Event
                </button>
              ) : null}
              <button
                className="esv-btn"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleModal();
                }}
              >
                <i className="fa-solid fa-trash me-2"></i>Delete Event
              </button>
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={modal} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>Delete Event</ModalHeader>
        <ModalBody className="text-center text-danger">
          Are you sure you want to delete the event <br />
          <strong>{eventName}</strong>?
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
          >
            Delete
          </Button>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <ToastContainer />
    </div>
  );
};
export default VenueEventListCard;
